import React, { startTransition, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import clientService from "src/clientService";
import { useSnackbar } from "src/view/components/SnackbarService";
import loginBg from "src/resources/images/login_bg.jpg";
import logoImg from "src/resources/images/logo-black 1.png";

const PasswordResetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { showSnackbar, snackbarType } = useSnackbar();

  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidLink, setIsValidLink] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Step 1: Verify token on mount
  useEffect(() => {
    const verifyLink = async () => {
      if (!token) {
        setIsVerifying(false);
        showSnackbar({ message: "Invalid reset link", type: snackbarType.ERROR });
        return;
      }

      try {
        await clientService.api.verifyPasswordResetLinkApiV1VerifyPasswordLinkPost({ token });
        setIsValidLink(true);
      } catch (error: any) {
        const msg = error.response?.data?.detail || "Invalid or expired reset link.";
        showSnackbar({ message: msg, type: snackbarType.ERROR });
        setIsValidLink(false);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyLink();
  }, [token]);

  // Step 2: Handle password reset
  const handleResetPassword = async () => {
    if (!newPassword) {
      showSnackbar({ message: "Please enter your new password.", type: snackbarType.WARNING });
      return;
    }

    setIsSubmitting(true);
    try {
      await clientService.api.resetUserPasswordApiV1ResetPasswordPost({
        token: token as string,
        new_password: newPassword,
      });

      showSnackbar({ message: "Password reset successful!" });
      startTransition(() => navigate('/admin'));
    } catch (error: any) {
      const msg = error.response?.data?.detail || "Failed to reset password.";
      showSnackbar({ message: msg, type: snackbarType.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 3: Render logic
  if (isVerifying) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${loginBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 3,
            padding: 4,
            textAlign: 'center',
            maxWidth: 450,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <CircularProgress />
          <Typography mt={2}>Verifying your reset link...</Typography>
        </Box>
      </Box>
    );
  }

  if (!isValidLink) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${loginBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 3,
            padding: 4,
            textAlign: 'center',
            maxWidth: 450,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Typography variant="h6" color="error">
            Invalid or expired password reset link.
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")} sx={{ mt: 2 }}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  // Step 4: Password reset form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${loginBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 3,
      }}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 3,
          padding: 4,
          maxWidth: 450,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <img 
            src={logoImg} 
            alt="Darukaa Earth Logo" 
            style={{ maxWidth: '200px', height: 'auto', marginBottom: '12px' }}
          />
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              fontSize: '0.9rem',
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            Innovating Carbon & Biodiversity Solutions
          </Typography>
        </Box>
        <Typography variant="h5" mb={3} textAlign="center">
          Reset Your Password
        </Typography>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            variant="outlined"
            margin="normal"
            disabled={isSubmitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isSubmitting}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleResetPassword}
            disabled={isSubmitting}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
          </Button>
      </Box>
    </Box>
  );
};

export default PasswordResetPage;
